import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import * as localService from "../services/localService";
import { usePaymentSetup } from "../hooks/usePaymentSetup";
import { fetchPaymentSetups, refreshPaymentSetups } from "../redux/actions/paymentSetup";
import PaymentSetupForm from "../screens/host-experience/host-payment-setup/components/PaymentSetupForm";
import { CreditCard } from "lucide-react";
import logger from "../utils/logger";


/**
 * PaymentSetupWall Component
 * Blocks access for HOST_PROPERTY_MANAGEMENT organizations until they complete their payment setup.
 *
 * Now uses Redux for state management (like SubscriptionWall):
 * - Payment setup is checked in postAuthFlow after login
 * - State is stored in Redux and accessed via usePaymentSetup hook
 * - Provides refresh mechanism via Redux dispatch
 */
const PaymentSetupWall = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const user = localService.getUser();

  // Determine if payment setup is required
  const { organization } = user || {};
  const orgTypeCode = organization?.type?.code;

  // Requirement: HOST_PROPERTY_MANAGEMENT and HOST_PROPERTY_DISTRIBUTION must add payment setup
  // ROLE_HOST_CUSTOMER_INDIVIDUAL is exempt even under these org types
  const userRoleCode = user?.organizationRole?.code || user?.role;
  const requiresPaymentSetup =
    (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "HOST_PROPERTY_DISTRIBUTION") &&
    userRoleCode !== "ROLE_HOST_CUSTOMER_INDIVIDUAL";

  // Get payment setup state from Redux (populated by postAuthFlow)
  const { loading, hasPaymentSetup, error, paymentSetupWallChecked } = usePaymentSetup();

  // Check for debug skip flag (only works on localhost)
  const isDebugSkip =
    window.location.hostname === "localhost" && localStorage.getItem("debug_skip_payment_setup") === "true";

  // Routes that are exempt from payment setup wall
  // IMPORTANT: Must include subscription routes to avoid blocking loops
  const exemptRoutes = [
    "/signin",
    "/signup",
    "/otp-confirmation",
    "/host/payment-setup",
    "/host/settings-payment-setup",
    // Subscription-related routes - allow access even without payment setup
    // This prevents blocking loop: user needs subscription → redirected to pricing → blocked by payment wall
    "/host/pricing",
    "/subscriber/pricing",
    "/host/subscription-upgrade",
  ];

  const isExemptRoute = exemptRoutes.some(route => location.pathname.startsWith(route));

  const handleSetupComplete = async () => {
    // Refresh the payment setup status from Redux
    await dispatch(refreshPaymentSetups());
  };

  // Log state for debugging
  useEffect(() => {
    if (requiresPaymentSetup) {
      logger.log("💳 PaymentSetupWall RENDER:", {
        orgTypeCode,
        hasPaymentSetup,
        requiresPaymentSetup,
        currentPath: location.pathname,
        isExemptRoute,
        loading,
        paymentSetupWallChecked,
      });
    }
  }, [
    orgTypeCode,
    hasPaymentSetup,
    requiresPaymentSetup,
    location.pathname,
    isExemptRoute,
    loading,
    paymentSetupWallChecked,
  ]);

  useEffect(() => {
    if (requiresPaymentSetup && !isExemptRoute && !isDebugSkip && !paymentSetupWallChecked && !loading) {
      dispatch(fetchPaymentSetups());
    }
  }, [requiresPaymentSetup, isExemptRoute, isDebugSkip, paymentSetupWallChecked, loading, dispatch]);

  // Show loading spinner while checking
  if (requiresPaymentSetup && !isExemptRoute && !isDebugSkip && (loading || !paymentSetupWallChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner
          loading={true}
          message="Checking payment configuration..."
          padding="py-8"
          className="text-blue-600"
        />
      </div>
    );
  }

  // Show error state if there was an error and we're not on exempt route
  if (requiresPaymentSetup && error && !isExemptRoute && !isDebugSkip) {
    // Check if error is authentication-related
    const isAuthError =
      error.includes("401") ||
      error.toLowerCase().includes("unauthorized") ||
      error.toLowerCase().includes("token") ||
      error.toLowerCase().includes("session");

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <div className="text-red-600 text-lg font-semibold mb-4">
            {isAuthError ? "Session Expired" : "Error checking payment setup"}
          </div>
          <div className="text-gray-600 mb-6">
            {isAuthError ? "Your session has expired. Please log in again to continue." : error}
          </div>
          <div className="flex gap-3 justify-center">
            {!isAuthError && (
              <button
                onClick={async () => {
                  // Retry the check
                  await dispatch(refreshPaymentSetups());
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            )}
            <button
              onClick={() => {
                localService.clearAllUserData();
                history.push("/signin");
              }}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              {isAuthError ? "Log In Again" : "Logout"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // IMMEDIATE BLOCK - This runs on every render BEFORE useEffect
  // If setup is required but missing, and not on an exempt route, block immediately
  if (requiresPaymentSetup && paymentSetupWallChecked && !hasPaymentSetup && !isExemptRoute && !isDebugSkip) {
    logger.log("💳 BLOCKING ACCESS - No payment setup, preventing render", {
      requiresPaymentSetup,
      hasPaymentSetup,
      paymentSetupWallChecked,
      isExemptRoute,
      currentPath: location.pathname,
      orgTypeCode,
    });

    return (
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Simple Header */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-2 rounded-lg text-white">
              <CreditCard size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Payment Setup Required</h1>
              <p className="text-sm text-gray-500">Configure how you receive payments to continue</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {window.location.hostname === "localhost" && (
              <button
                onClick={() => {
                  localStorage.setItem("debug_skip_payment_setup", "true");
                  window.location.reload();
                }}
                className="px-4 py-2 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors border border-orange-300"
              >
                Skip (Debug)
              </button>
            )}
            <button
              onClick={() => {
                localService.clearAllUserData();
                history.push("/signin");
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-12" style={{ height: "calc(100vh - 80px)" }}>
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-blue-800">
              <h2 className="font-semibold text-lg mb-2">Welcome to your Host Dashboard!</h2>
              <p className="text-blue-700 leading-relaxed">
                Before you can start managing your spaces and receiving bookings, we need to know where to send your
                payments. Please add a primary payment method (Bank Account or M-Pesa) below.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
              <PaymentSetupForm
                onComplete={handleSetupComplete}
                // No 'onPrevious' here as we want to lock them in until complete
              />
            </div>

            <div className="text-center text-gray-400 text-sm py-8">
              &copy; {new Date().getFullYear()} Spacia. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    );
  }

  logger.log("💳 PaymentSetupWall - Rendering children, all checks passed");

  // Render children if all checks pass
  return children;
};

export default PaymentSetupWall;
